import type { UseOrganizationUsersFetch } from '@openx/components-organization/lib/utils/context/OrganizationHookTypes';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GetOrganizationUsersDocument } from '../../../graphql/query/organizations/GetOrganizationUsers.generated';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types';

const PAGE_SIZE = 20;

export const useOrganizationUsersFetch: UseOrganizationUsersFetch = organizationId => {
  const { data, isLoading, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    enabled: !!organizationId,
    queryFn: ({ client, pageParam }) =>
      getGraphQLRequestFromClient(client)(GetOrganizationUsersDocument, {
        id: organizationId,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage.account_by_pk?.users_aggregate.aggregate?.count ?? 0;
      const loadedCount = allPages.reduce((sum, page) => sum + (page.account_by_pk?.users.length ?? 0), 0);

      return loadedCount < totalCount ? loadedCount : undefined;
    },
    queryKey: [QueryKeys.ORGANIZATION_USERS, organizationId],
  });

  const users = useMemo(() => (data?.pages ?? []).flatMap(page => page.account_by_pk?.users ?? []), [data]);
  const totalCount = data?.pages[0]?.account_by_pk?.users_aggregate.aggregate?.count ?? 0;

  return {
    hasMore: users.length < totalCount,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    showMore: fetchNextPage,
    users,
  };
};
